/**
 * Token validation and session management utilities.
 *
 * All decisions about whether a session is trustworthy live here.
 * Every other part of the app must go through these functions —
 * never roll your own token checks inline.
 */

const SESSION_KEYS = [
  'guest_token',
  'guest_profile',
  'guest_hotel_id',
  'guest_hotel_name',
  'guest_hotel_slug',
] as const;

/** Decode the payload of a JWT without verifying the signature. */
function parseJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    // base64url → base64 → JSON
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded  = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
    const json    = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Returns true if the token's `exp` claim is in the past.
 * Tokens with no `exp` claim are treated as non-expiring (valid).
 * Malformed tokens (not parseable) are treated as expired.
 */
export function isTokenExpired(token: string): boolean {
  const payload = parseJwtPayload(token);
  if (!payload) return true; // unparseable = treat as expired / corrupted

  if (typeof payload.exp !== 'number') return false; // no expiry = always valid

  // Add a 10-second clock-skew buffer
  return Date.now() / 1000 > payload.exp - 10;
}

/**
 * Returns true only if:
 * - token is a non-empty string
 * - it has the three-part JWT structure (header.payload.signature)
 * - it is not expired
 *
 * This does NOT verify the signature — that is the server's job.
 * We only catch obviously invalid / stale / tampered tokens client-side.
 */
export function isTokenTrusted(token: string | null | undefined): token is string {
  if (!token || typeof token !== 'string') return false;
  if (token.split('.').length !== 3)        return false; // not a JWT
  if (isTokenExpired(token))                return false;
  return true;
}

/**
 * Wipe every piece of session state from localStorage.
 * Call this on logout, 401, 403 with active session, or any trust violation.
 *
 * device_fp is intentionally kept — it is not sensitive and
 * its presence helps with QR re-binding at reception.
 */
export function clearSession(): void {
  SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
}
